import type { TFunction } from 'i18next';
import type { ChecklistCard, ChecklistDraftInput, ChecklistEditor, ChecklistTranslationItem } from '../../../api/safetyRounds';

/** A checkpoint while HR edits it. New ones have a local id and no key. */
export type DraftCheckpoint = { id: string; key?: string; text: string; help: string | null; required: boolean };
export type DraftCategory = { id: string; key?: string; name: string; checkpoints: DraftCheckpoint[] };
export type Draft = { name: string; description: string; categories: DraftCategory[] };

let counter = 0;
const localId = () => `new-${(counter += 1)}`;

export const toDraft = (editor: Pick<ChecklistEditor, 'editing'>): Draft => ({
  name: editor.editing.name,
  description: editor.editing.description,
  categories: editor.editing.categories.map((category) => ({
    id: category.key,
    key: category.key,
    name: category.name,
    checkpoints: category.checkpoints.map((point) => ({
      id: point.key,
      key: point.key,
      text: point.text,
      help: point.help,
      required: point.required,
    })),
  })),
});

export const toDraftInput = (draft: Draft): ChecklistDraftInput => ({
  name: draft.name,
  description: draft.description,
  categories: draft.categories.map((category) => ({
    key: category.key,
    name: category.name,
    checkpoints: category.checkpoints.map(({ key, text, help, required }) => ({ key, text, help, required })),
  })),
});

/** «4.5»: the fifth checkpoint in the fourth category, as in the protocol */
export const checkpointNumber = (categoryIndex: number, index: number) => `${categoryIndex + 1}.${index + 1}`;

export const addCategory = (draft: Draft): Draft => ({
  ...draft,
  categories: [
    ...draft.categories,
    { id: localId(), name: '', checkpoints: [{ id: localId(), text: '', help: null, required: false }] },
  ],
});

export const removeCategory = (draft: Draft, categoryId: string): Draft => ({
  ...draft,
  categories: draft.categories.filter((category) => category.id !== categoryId),
});

const updateCategory = (draft: Draft, categoryId: string, change: (category: DraftCategory) => DraftCategory) => ({
  ...draft,
  categories: draft.categories.map((category) => (category.id === categoryId ? change(category) : category)),
});

export const renameCategory = (draft: Draft, categoryId: string, name: string): Draft =>
  updateCategory(draft, categoryId, (category) => ({ ...category, name }));

export const addCheckpoint = (draft: Draft, categoryId: string): Draft =>
  updateCategory(draft, categoryId, (category) => ({
    ...category,
    checkpoints: [...category.checkpoints, { id: localId(), text: '', help: null, required: false }],
  }));

export const updateCheckpoint = (
  draft: Draft,
  categoryId: string,
  checkpointId: string,
  change: Partial<Pick<DraftCheckpoint, 'text' | 'required'>>,
): Draft =>
  updateCategory(draft, categoryId, (category) => ({
    ...category,
    checkpoints: category.checkpoints.map((point) => (point.id === checkpointId ? { ...point, ...change } : point)),
  }));

export const removeCheckpoint = (draft: Draft, categoryId: string, checkpointId: string): Draft =>
  updateCategory(draft, categoryId, (category) => ({
    ...category,
    checkpoints: category.checkpoints.filter((point) => point.id !== checkpointId),
  }));

/** Whether HR has changed anything since the page loaded or last saved */
export const isChanged = (draft: Draft, saved: Draft) =>
  JSON.stringify(toDraftInput(draft)) !== JSON.stringify(toDraftInput(saved));

export const CHECKLIST_FIELD_IDS = {
  name: 'checklist-name',
  category: (id: string) => `checklist-category-${id}`,
  addCheckpoint: (id: string) => `checklist-add-checkpoint-${id}`,
  addCategory: 'checklist-add-category',
  changeNote: 'checklist-change-note',
};

export type ChecklistFormError = { fieldId: string; message: string };

/**
 * What stops HR from publishing, as the API checks it. Empty checkpoints are left out when
 * publishing, so they are not errors.
 */
export const publishFormErrors = (draft: Draft, changeNote: string, t: TFunction): ChecklistFormError[] => {
  const K = 'safetyRounds.checklist.errors';
  const categories = draft.categories
    .map((category) => ({ ...category, checkpoints: category.checkpoints.filter((point) => point.text.trim()) }))
    .filter((category) => category.checkpoints.length > 0 || category.name.trim());
  return [
    ...(draft.name.trim() ? [] : [{ fieldId: CHECKLIST_FIELD_IDS.name, message: t(`${K}.name`) }]),
    ...categories.flatMap((category, index) => [
      ...(category.name.trim()
        ? []
        : [{ fieldId: CHECKLIST_FIELD_IDS.category(category.id), message: t(`${K}.categoryName`, { number: index + 1 }) }]),
      ...(category.checkpoints.length > 0
        ? []
        : [
            {
              fieldId: CHECKLIST_FIELD_IDS.addCheckpoint(category.id),
              message: t(`${K}.emptyCategory`, { name: category.name.trim() }),
            },
          ]),
    ]),
    ...(categories.some((category) => category.checkpoints.length > 0)
      ? []
      : [{ fieldId: CHECKLIST_FIELD_IDS.addCategory, message: t(`${K}.noCheckpoints`) }]),
    ...(changeNote.trim() ? [] : [{ fieldId: CHECKLIST_FIELD_IDS.changeNote, message: t(`${K}.changeNote`) }]),
  ];
};

/** «Versjon 3 · 24 punkter · 3 påkrevde» style facts for a card */
export const cardStatus = (card: Pick<ChecklistCard, 'status' | 'version' | 'hasDraft'>, t: TFunction) => {
  const K = 'safetyRounds.checklists.status';
  if (card.status === 'archived') return { label: t(`${K}.archived`), color: 'neutral' as const };
  if (card.status === 'draft') return { label: t(`${K}.draft`), color: 'warning' as const };
  return card.hasDraft
    ? { label: t(`${K}.publishedWithDraft`), color: 'info' as const }
    : { label: t(`${K}.published`), color: 'success' as const };
};

export type TranslationGroup = { title: string | null; items: ChecklistTranslationItem[] };

/** The template's name and description first, then one group per category, in checklist order */
export const groupTranslations = (items: ChecklistTranslationItem[]): TranslationGroup[] =>
  items.reduce<TranslationGroup[]>((groups, item) => {
    const last = groups[groups.length - 1];
    return last && last.title === item.categoryName
      ? [...groups.slice(0, -1), { ...last, items: [...last.items, item] }]
      : [...groups, { title: item.categoryName, items: [item] }];
  }, []);

export const textsToSuggest = (items: ChecklistTranslationItem[]) =>
  items.filter((item) => !item.text.trim() && item.standard === null);

export const translationLabel = (item: ChecklistTranslationItem, t: TFunction) =>
  t(`safetyRounds.checklist.translate.kinds.${item.kind}`, { number: item.number });
